export enum ClinicalDocumentType {
  HISTORY_AND_PHYSICAL = 'history-and-physical',
  ABA_NOTES = 'aba-notes',
  SPEECH_NOTES = 'speech-notes',
  DIAGNOSIS_LIST = 'diagnosis-list',
  MEDICATION_LIST = 'medication-list',
  THERAPY_NOTES = 'therapy-notes',
  OCCUPATIONAL_THERAPY_NOTES = 'occupational-therapy-notes',
  PHYSICAL_THERAPY_NOTES = 'physical-therapy-notes',
  SPEECH_THERAPY_NOTES = 'speech-therapy-notes',
  EQUIPMENT_NOTES = 'equipment-notes',
  SPECIALIST_NOTES = 'specialist-notes',
  G_TUBE_SCHEDULE = 'g-tube-schedule',
  CORHIO_DOCS = 'corhio-docs',
  OT_HEP = 'ot-hep',
  PT_HEP = 'pt-hep',
  INTAKE_FORM = 'intake-form',
  DME_ORDER = 'dme-order',
  ROC_ORDER = 'roc-order',
  VERBAL_ORDER = 'verbal-order',
  SIGNED_VERBAL_ORDER = 'signed-verbal-order',
  HOLD_ORDER = 'hold-order',
  MED_RECONCILIATION = 'med-reconciliation',
  ADVANCED_DIRECTIVE = 'advanced-directive',
  DISCHARGE_SUMMARY = 'discharge-summary',
  TRANSFER_SUMMARY = 'transfer-summary',
  RISK_ASSESSMENT = 'risk-assessment',
  FSA = 'fsa',
  BRADEN_SCALE = 'braden-scale',
  GENERAL_ORDER = 'general-order',
  LETTER_OF_MEDICAL_NECESSITY = 'letter-of-medical-necessity',
  PRIOR_AUTH_APPROVALS = 'prior-auth-approvals',
  VISIT_NOTES = 'visit-notes',
  MEDICAL_RECORDS = 'medical-records',
  IEP = 'iep',
  OTHER = 'other',
  EXPORTED_FACESHEET = 'exported-facesheet',
  EXPORTED_ASSESSMENT = 'exported-assessment',
  EXPORTED_VISIT_NOTES = 'exported-visit-notes',
  SERVICE_PLAN = 'service-plan'
}

export enum PatientDocumentType {
  MAR = 'mar'
}

export enum EmployeeDocumentType {
  TB = 'tb',
  COVID = 'covid',
  FLU = 'flu',
  BACKGROUND_CHECK = 'background-check',
  CAPS_CHECK = 'caps-check',
  OIG_CHECK = 'oig-check',
  CPR = 'CPR',
  CNA_LICENSE = 'cna-license',
  HHA_LICENSE = 'hha-license',
  RN_LICENSE = 'rn-license',
  TEMPORARY_LICENSE = 'temporary-license',
  EMPLOYEE_HANDBOOK = 'employee-handbook',
  SUP_AND_COMP = 'sup-and-comp',
  SIGNED_JOB_DESCRIPTION = 'signed-job-description',
  CHANGE_OF_PROVIDER_FORM = 'change-of-provider-form',
  HEPATITIS = 'hepatitis',
  RELIAS = 'relias',
  HR = 'hr',
  COL = 'col',
  HHA_SKILLS_CHECKLIST = 'hha-skills-checklist',
  PAPER_CHARTING_BACK_CHARTING = 'paper-charting-back-charting'
}

export enum ComplianceDocumentType {
  EVV = 'evv',
  F2F_SUMMARY = 'f2f-summary',
  F2F_SUMMARY_WITH_HHC_AUTH = 'f2f-summary-with-hhc-auth',
  PATIENT_HANDBOOK = 'patient-handbook',
  ROI = 'roi'
}

export enum OrdersDocumentType {
  PAT = 'pat',
  CARE_PLAN = 'care-plan',
  SIGNED_CARE_PLAN = 'signed-care-plan',
  SIGNED_ORDER = 'signed-order',
  UNSIGNED_ORDER = 'unsigned-order',
  DISCHARGE_SUMMARY = 'discharge-summary'
}

export enum NeedsForDocumentType {
  AMBULATION = 'ambulation',
  BATHING = 'bathing',
  BRACES_SPLINTS = 'braces-splints',
  DIET_ORDERS = 'diet-orders',
  FEEDING_ORDERS = 'feeding-orders',
  INCONTINENCE = 'incontinence',
  NONVERBAL = 'nonverbal',
  PAIN = 'pain',
  SEIZURES = 'seizures',
  SKIN_CREAM_RX = 'skin-cream-rx',
  SKIN_DISORDER = 'skin-disorder',
  TRANSFER_ASSIST = 'transfer-assist'
}

export type DocumentType =
  | NeedsForDocumentType
  | ClinicalDocumentType
  | ComplianceDocumentType
  | OrdersDocumentType
  | PatientDocumentType
  | EmployeeDocumentType

// https://coda.io/d/Product_dOGvGHqvHdP/LOINC-Codes_suDCq#Codes_tutsX
// These are used directly within Salesforce, so do not change the
// labels.
export const CLINICAL_DOCUMENT_TYPES_AND_CODES: Record<
  ClinicalDocumentType,
  { label: string; code: string; isAbbyCareCodeSystem?: boolean }
> = {
  [ClinicalDocumentType.HISTORY_AND_PHYSICAL]: {
    label: 'History and Physical',
    code: '34117-2'
  },
  [ClinicalDocumentType.ABA_NOTES]: {
    label: 'ABA Notes',
    code: '68608-9'
  },
  [ClinicalDocumentType.DIAGNOSIS_LIST]: {
    label: 'Diagnosis List',
    code: '60591-5'
  },
  [ClinicalDocumentType.MEDICATION_LIST]: {
    label: 'Medication List',
    code: '56445-0'
  },
  [ClinicalDocumentType.THERAPY_NOTES]: {
    label: 'Therapy Notes',
    code: '34109-9'
  },
  [ClinicalDocumentType.OCCUPATIONAL_THERAPY_NOTES]: {
    label: 'Occupational Therapy Notes',
    code: 'occupational-therapy-notes'
  },
  [ClinicalDocumentType.PHYSICAL_THERAPY_NOTES]: {
    label: 'Physical Therapy Notes',
    code: 'physical-therapy-notes'
  },
  [ClinicalDocumentType.SPEECH_THERAPY_NOTES]: {
    label: 'Speech Therapy Notes',
    code: 'speech-therapy-notes'
  },
  [ClinicalDocumentType.EQUIPMENT_NOTES]: {
    label: 'Equipment Notes',
    code: '82215-5'
  },
  [ClinicalDocumentType.SPEECH_NOTES]: {
    label: 'Speech Notes',
    code: '52410-8'
  },
  [ClinicalDocumentType.SPECIALIST_NOTES]: {
    label: 'Specialist Notes',
    code: 'LP72311-1'
  },
  [ClinicalDocumentType.PT_HEP]: {
    label: 'PT HEP',
    code: '52289-6'
  },
  [ClinicalDocumentType.OT_HEP]: {
    label: 'OT HEP',
    code: '52258-1'
  },
  [ClinicalDocumentType.G_TUBE_SCHEDULE]: {
    label: 'G-Tube Feed Schedule',
    code: '92183-3'
  },
  [ClinicalDocumentType.CORHIO_DOCS]: {
    label: 'CORHIO Docs',
    code: '57041-6'
  },
  [ClinicalDocumentType.INTAKE_FORM]: {
    label: 'Intake Form',
    code: '55188-7'
  },
  [ClinicalDocumentType.DME_ORDER]: {
    label: 'DME Order',
    code: 'dme-order'
  },
  [ClinicalDocumentType.ROC_ORDER]: {
    label: 'ROC Order',
    code: '57201-6'
  },
  [ClinicalDocumentType.VERBAL_ORDER]: {
    label: 'Verbal Order',
    code: 'verbal-order',
    isAbbyCareCodeSystem: true
  },
  [ClinicalDocumentType.SIGNED_VERBAL_ORDER]: {
    label: 'Signed Verbal Order',
    code: 'signed-verbal-order',
    isAbbyCareCodeSystem: true
  },
  [ClinicalDocumentType.HOLD_ORDER]: {
    label: 'Hold Order',
    code: '75476-2'
  },
  [ClinicalDocumentType.MED_RECONCILIATION]: {
    label: 'Med Reconciliation',
    code: '80820-4'
  },
  [ClinicalDocumentType.ADVANCED_DIRECTIVE]: {
    label: 'Advanced Directive',
    code: '93442-2'
  },
  [ClinicalDocumentType.DISCHARGE_SUMMARY]: {
    label: 'Discharge Summary',
    code: '74213-0'
  },
  [ClinicalDocumentType.TRANSFER_SUMMARY]: {
    label: 'Transfer Summary',
    code: '18761-7'
  },
  [ClinicalDocumentType.RISK_ASSESSMENT]: {
    label: 'Risk Assessment',
    code: '71482-4'
  },
  [ClinicalDocumentType.FSA]: {
    label: 'FSA',
    code: '64299-1'
  },
  [ClinicalDocumentType.BRADEN_SCALE]: {
    label: 'Braden Scale',
    code: 'branden-scale',
    isAbbyCareCodeSystem: true
  },
  [ClinicalDocumentType.GENERAL_ORDER]: {
    label: 'General order',
    code: 'general-order',
    isAbbyCareCodeSystem: true
  },
  [ClinicalDocumentType.LETTER_OF_MEDICAL_NECESSITY]: {
    label: 'Letter of Medical Necessity (LMN)',
    code: '103595-5'
  },
  [ClinicalDocumentType.PRIOR_AUTH_APPROVALS]: {
    label: 'Prior Auth Approvals',
    code: 'prior-auth-approvals',
    isAbbyCareCodeSystem: true
  },
  [ClinicalDocumentType.VISIT_NOTES]: {
    label: 'Visit Notes',
    code: '11506-3'
  },
  [ClinicalDocumentType.MEDICAL_RECORDS]: {
    label: 'Medical Records',
    code: 'medical-records'
  },
  [ClinicalDocumentType.IEP]: {
    label: 'IEP',
    code: 'iep'
  },
  [ClinicalDocumentType.OTHER]: {
    label: 'Other',
    code: '51898-6'
  },
  [ClinicalDocumentType.EXPORTED_FACESHEET]: {
    label: 'Exported Facesheet',
    code: 'exported-facesheet',
    isAbbyCareCodeSystem: true
  },
  [ClinicalDocumentType.EXPORTED_ASSESSMENT]: {
    label: 'Exported Assessment',
    code: 'exported-assessment',
    isAbbyCareCodeSystem: true
  },
  [ClinicalDocumentType.EXPORTED_VISIT_NOTES]: {
    label: 'Exported Visit Notes',
    code: 'exported-visit-notes',
    isAbbyCareCodeSystem: true
  },
  [ClinicalDocumentType.SERVICE_PLAN]: {
    label: 'Service Plan',
    code: 'service-plan',
    isAbbyCareCodeSystem: true
  }
}

export enum NotApplicableDocumentType {
  NOT_APPLICABLE = 'not-applicable'
}

export const NOT_APPLICABLE_DOCUMENT_TYPE: Record<
  NotApplicableDocumentType,
  { label: string; code: string }
> = {
  [NotApplicableDocumentType.NOT_APPLICABLE]: {
    label: 'N/A',
    code: NotApplicableDocumentType.NOT_APPLICABLE
  }
}

export const COMPLIANCE_DOCUMENT_TYPES_AND_CODES: Record<
  ComplianceDocumentType,
  { label: string; code: string }
> = {
  [ComplianceDocumentType.EVV]: {
    label: 'EVV',
    code: '64299-1'
  },
  [ComplianceDocumentType.F2F_SUMMARY]: {
    label: 'F2F Summary',
    code: '60591-5'
  },
  [ComplianceDocumentType.F2F_SUMMARY_WITH_HHC_AUTH]: {
    label: 'F2F with HHC Auth',
    code: '60591-5'
  },
  [ComplianceDocumentType.PATIENT_HANDBOOK]: {
    label: 'Patient Handbook',
    code: '64299-1'
  },
  [ComplianceDocumentType.ROI]: {
    label: 'ROI',
    code: '64292-6'
  }
}
export const ORDERS_DOCUMENT_TYPES_AND_CODES: Record<
  OrdersDocumentType,
  { label: string; code: string }
> = {
  [OrdersDocumentType.PAT]: {
    label: 'PAT',
    code: '51848-0'
  },
  [OrdersDocumentType.CARE_PLAN]: {
    label: 'Care Plan',
    code: '18776-5'
  },
  [OrdersDocumentType.SIGNED_CARE_PLAN]: {
    label: 'Signed Care Plan',
    code: '18776-5'
  },
  [OrdersDocumentType.SIGNED_ORDER]: {
    label: 'Signed Order',
    code: '60591-5'
  },
  [OrdersDocumentType.UNSIGNED_ORDER]: {
    label: 'Unsigned Order',
    code: '60591-5'
  },
  [OrdersDocumentType.DISCHARGE_SUMMARY]: {
    label: 'Discharge Summary',
    code: '18842-5'
  }
}

export const PATIENT_DOCUMENT_TYPES_AND_CODES: Record<
  PatientDocumentType,
  { label: string; code: string }
> = {
  [PatientDocumentType.MAR]: {
    label: 'MAR',
    code: '80565-5'
  }
}

export const EMPLOYEE_DOCUMENT_TYPES_AND_CODES: Record<
  EmployeeDocumentType,
  { label: string; code: string }
> = {
  [EmployeeDocumentType.TB]: {
    label: 'TB',
    code: '92183-3'
  },
  [EmployeeDocumentType.COVID]: {
    label: 'Covid',
    code: '92183-3'
  },
  [EmployeeDocumentType.FLU]: {
    label: 'Flu',
    code: '92183-3'
  },
  [EmployeeDocumentType.BACKGROUND_CHECK]: {
    label: 'Background Check',
    code: '64299-1'
  },
  [EmployeeDocumentType.CAPS_CHECK]: {
    label: 'CAPS Check',
    code: '64299-1'
  },
  [EmployeeDocumentType.OIG_CHECK]: {
    label: 'OIG Check',
    code: '64299-1'
  },
  [EmployeeDocumentType.CPR]: {
    label: 'CPR',
    code: '75535-5'
  },
  [EmployeeDocumentType.CNA_LICENSE]: {
    label: 'CNA License',
    code: '75535-5'
  },
  [EmployeeDocumentType.HHA_LICENSE]: {
    label: 'HHA License',
    code: '75535-5'
  },
  [EmployeeDocumentType.RN_LICENSE]: {
    label: 'RN License',
    code: '64299-1'
  },
  [EmployeeDocumentType.TEMPORARY_LICENSE]: {
    label: 'Temporary License',
    code: '64299-1'
  },
  [EmployeeDocumentType.EMPLOYEE_HANDBOOK]: {
    label: 'Employee Handbook',
    code: '64299-1'
  },
  [EmployeeDocumentType.SUP_AND_COMP]: {
    label: 'Sup and Comp',
    code: '64299-1'
  },
  [EmployeeDocumentType.SIGNED_JOB_DESCRIPTION]: {
    label: 'Signed Job Description',
    code: '64299-1'
  },
  [EmployeeDocumentType.CHANGE_OF_PROVIDER_FORM]: {
    label: 'Change of Provider',
    code: '64299-1'
  },
  [EmployeeDocumentType.HEPATITIS]: {
    label: 'Hepatitis',
    code: '55112-7'
  },
  [EmployeeDocumentType.RELIAS]: {
    label: 'Relias',
    code: '34895-3'
  },
  [EmployeeDocumentType.HR]: {
    label: 'HR',
    code: '64299-1'
  },
  [EmployeeDocumentType.COL]: {
    label: 'Conditional Offer Letter',
    code: '64299-1'
  },
  [EmployeeDocumentType.HHA_SKILLS_CHECKLIST]: {
    label: 'HHA Skills Checklist',
    code: '64299-1'
  },
  [EmployeeDocumentType.PAPER_CHARTING_BACK_CHARTING]: {
    label: 'Paper Charting / Back Charting',
    code: '64299-1'
  }
}

export const NEEDS_FOR_DOCUMENT_TYPES_AND_CODES: Record<
  NeedsForDocumentType,
  { label: string; code: string }
> = {
  [NeedsForDocumentType.AMBULATION]: {
    label: 'Ambulation',
    code: '83186-7'
  },
  [NeedsForDocumentType.BATHING]: {
    label: 'Bathing',
    code: '83181-8'
  },
  [NeedsForDocumentType.BRACES_SPLINTS]: {
    label: 'Braces/Splints',
    code: '83182-6'
  },
  [NeedsForDocumentType.DIET_ORDERS]: {
    label: 'Diet orders',
    code: '57249-5'
  },
  [NeedsForDocumentType.FEEDING_ORDERS]: {
    label: 'Feeding orders',
    code: '83184-2'
  },
  [NeedsForDocumentType.INCONTINENCE]: {
    label: 'Incontinence (bowel/bladder)',
    code: '83183-4'
  },
  [NeedsForDocumentType.NONVERBAL]: {
    label: 'Non-verbal',
    code: '52621-0'
  },
  [NeedsForDocumentType.PAIN]: {
    label: 'Pain',
    code: '80316-3'
  },
  [NeedsForDocumentType.SEIZURES]: {
    label: 'Seizures',
    code: '99312-1'
  },
  [NeedsForDocumentType.SKIN_CREAM_RX]: {
    label: 'Skin Cream Prescription',
    code: '45606-1'
  },
  [NeedsForDocumentType.SKIN_DISORDER]: {
    label: 'Skin Disorder (eczema/wounds)',
    code: '81636-3'
  },
  [NeedsForDocumentType.TRANSFER_ASSIST]: {
    label: 'Transfer assist',
    code: '83185-9'
  }
}
